import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router";
import { SAVE_TEST } from '../../utils/Constants';
import { ABContext } from '../../contexts/ABContext';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ChevronLeft } from 'Components/DesignSystem/Iconography/Icons';

type TABDetailHeaderProps = {
    testName?: string;
    onSave: () => Promise<boolean | undefined>;
}

/**
 * The wizard header (`okf/design-system/e2e.md` § 148). Same treatment as § 139's list
 * header: the title was a `<label>` with no `htmlFor` at a hardcoded
 * `tw-text-[24px] tw-font-semibold`, and is now `Typography heading-2 as="h1"`.
 *
 * `id="save-button"` on the row is **load-bearing and must stay**:
 * `TestDesignSection.scrollToSaveButton` does `document.querySelector('#save-button')`
 * to scroll here when Calculate Test Duration needs the test saved first.
 *
 * The back control keeps its `ab-detail-back-btn` testid and gains a focus ring it
 * never had — it was a bare `<button>` wrapping an SVG.
 *
 * **Save is disabled for the duration of the request.** `saveTest` is an async PUT and
 * the button had no busy state at all, so a second click fired a second
 * `updateAbTest` with the same payload while the first was still open — two writes
 * racing, and the later response winning regardless of which was sent first. The label
 * changes too: a button that is merely greyed with no other signal reads as broken
 * rather than working.
 *
 * The row itself is no longer tinted. It was `tw-bg-[#FAFAFC]` — an off-white that is
 * not a design-system surface, and which made the header look like a disabled strip
 * sitting above a white page.
 */
const ABDetailHeader = ({ testName, onSave }: TABDetailHeaderProps) => {
    const navigate = useNavigate();
    const { clearData } = useContext(ABContext);
    const [saving, setSaving] = useState(false);

    /* `saveTest` does not navigate, so this header stays mounted across the await —
       but the guard costs one ref and removes the whole class of "setState on an
       unmounted tree" that this module has already produced twice. */
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    const handleSave = async () => {
        /*
          No `if (saving) return` re-entrancy guard here. One was written and then
          removed: `disabled={saving}` already stops the second click — in the browser
          and in the test — so the guard could not be made to fail, and § 129's
          `TimePicker` lesson applies. A guard no test can falsify is a guard nobody
          can maintain.
        */
        setSaving(true);
        try {
            await onSave();
        } catch (error) {
            /*
              Caught, not merely `finally`-ed. A rejected `onSave` inside a React click
              handler becomes an UNHANDLED REJECTION — vitest fails the run on it, and in
              the browser it reaches `window.onunhandledrejection`. `ABDetailPage.saveTest`
              happens to try/catch internally so it never rejects today, but the button
              must not depend on that.

              Swallowed rather than rethrown, and logged rather than toasted: reporting
              the failure is `onSave`'s job (it already does), and a second message here
              would double up.
            */
            console.error('ABDetailHeader ~ save failed:', error);
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    const onBackPress = () => {
        navigate("/gms/ab", { replace: true });
        clearData();
    }

    return (
        <div id="save-button" className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-edge tw-py-1.5">
            <div className="tw-flex tw-items-center tw-gap-3">
                <Button
                    variant="subtle"
                    size="sm"
                    onClick={onBackPress}
                    data-testid="ab-detail-back-btn"
                    showIcon
                    icon={<ChevronLeft />}
                >
                    {/*
                      An sr-only child, NOT `ariaLabel`. Both would name the button
                      twice from two independent sources — the WCAG 2.5.3 hazard
                      `Toggle` was fixed for. Caught by falsification: removing
                      `ariaLabel` changed nothing, because the child was already doing
                      the work.
                    */}
                    <span className="tw-sr-only">Back</span>
                </Button>
                <Typography
                    variant="display"
                    as="h1"
                    className="tw-text-ink"
                    data-testid="ab-detail-title"
                >
                    {testName ?? ''}
                </Typography>
            </div>
            <Button
                variant="primary"
                data-testid="ab-detail-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="tw-min-w-[104px]"
            >
                {saving ? 'Saving...' : SAVE_TEST}
            </Button>
        </div>
    );
}

export default ABDetailHeader;
